package com.klantenstop.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.materialkobweb.components.widgets.DsMaterialSymbols
import com.materialkobweb.styles.MaterialColorVars
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

// ─── Type Aliases ─────────────────────────────────────────────────────────────

private typealias KeyboardHandler = (Event) -> Unit

// ─── Constants ────────────────────────────────────────────────────────────────

private const val SELECT_Z_INDEX = 1000
private const val SELECT_MAX_HEIGHT = 240
private const val SELECT_SHADOW_ALPHA = 0.15f
private const val SELECT_MENU_HEIGHT_ESTIMATE = 270

sealed interface CustomSelectKind : ComponentKind
sealed interface CustomSelectTriggerKind : ComponentKind
sealed interface CustomSelectMenuKind : ComponentKind
sealed interface CustomSelectOptionKind : ComponentKind

/** CSS custom properties for [CustomSelect]. Override via `setVariable()` on parent or modifier. */
object CustomSelectVars {
    val BorderColor by StyleVariable<CSSColorValue>(prefix = "klantenstop")
    val BorderActiveColor by StyleVariable<CSSColorValue>(prefix = "klantenstop")
    val TriggerBackgroundColor by StyleVariable<CSSColorValue>(prefix = "klantenstop")
    val MenuBackgroundColor by StyleVariable<CSSColorValue>(prefix = "klantenstop")
    val SelectedOptionBackgroundColor by StyleVariable<CSSColorValue>(prefix = "klantenstop")
    val TriggerHeight by StyleVariable<CSSLengthNumericValue>(prefix = "klantenstop", defaultFallback = 36.px)
    val TriggerBorderRadius by StyleVariable<CSSLengthNumericValue>(prefix = "klantenstop", defaultFallback = 4.px)
    val TriggerPaddingHorizontal by StyleVariable<CSSLengthNumericValue>(prefix = "klantenstop", defaultFallback = 0.625.cssRem)
    val MenuBorderRadius by StyleVariable<CSSLengthNumericValue>(prefix = "klantenstop", defaultFallback = 6.px)
    val MenuMaxHeight by StyleVariable<CSSLengthNumericValue>(prefix = "klantenstop", defaultFallback = SELECT_MAX_HEIGHT.px)
}

// ─── Styles ───────────────────────────────────────────────────────────────────

private fun activeBorderModifier() = Modifier
    .border(1.px, LineStyle.Solid, CustomSelectVars.BorderActiveColor.value())
    .boxShadow(spreadRadius = 1.px, color = CustomSelectVars.BorderActiveColor.value())

val CustomSelectStyle = CssStyle.base<CustomSelectKind> {
    Modifier.fillMaxWidth().position(Position.Relative)
}

val CustomSelectTriggerStyle = CssStyle<CustomSelectTriggerKind> {
    base {
        Modifier
            .setVariable(CustomSelectVars.BorderColor, MaterialColorVars.Outline.value())
            .setVariable(CustomSelectVars.BorderActiveColor, MaterialColorVars.Primary.value())
            .setVariable(CustomSelectVars.TriggerBackgroundColor, MaterialColorVars.SurfaceContainer.value())
            .height(CustomSelectVars.TriggerHeight.value())
            .fillMaxWidth()
            .backgroundColor(CustomSelectVars.TriggerBackgroundColor.value())
            .border(1.px, LineStyle.Solid, CustomSelectVars.BorderColor.value())
            .borderRadius(CustomSelectVars.TriggerBorderRadius.value())
            .padding(leftRight = CustomSelectVars.TriggerPaddingHorizontal.value())
            .cursor(Cursor.Pointer)
    }
    hover { activeBorderModifier() }
    focusVisible { activeBorderModifier() }
}

val CustomSelectMenuStyle = CssStyle<CustomSelectMenuKind> {
    base {
        Modifier
            .setVariable(CustomSelectVars.MenuBackgroundColor, MaterialColorVars.SurfaceContainerHighest.value())
            .fillMaxWidth()
            .position(Position.Absolute)
            .left(0.px)
            .zIndex(SELECT_Z_INDEX)
            .backgroundColor(CustomSelectVars.MenuBackgroundColor.value())
            .borderRadius(CustomSelectVars.MenuBorderRadius.value())
            .boxShadow(0.px, 4.px, 12.px, 0.px, Color.rgba(0, 0, 0, SELECT_SHADOW_ALPHA))
            .padding(topBottom = 8.px)
            .overflow(Overflow.Auto)
            .maxHeight(CustomSelectVars.MenuMaxHeight.value())
    }
}

val DownwardCustomSelectMenuVariant = CustomSelectMenuStyle.addVariant {
    base { Modifier.top(100.percent) }
}

val UpwardCustomSelectMenuVariant = CustomSelectMenuStyle.addVariant {
    base { Modifier.bottom(100.percent) }
}

val CustomSelectOptionStyle = CssStyle<CustomSelectOptionKind> {
    base {
        Modifier
            .setVariable(CustomSelectVars.SelectedOptionBackgroundColor, MaterialColorVars.SecondaryContainer.value())
            .fillMaxWidth()
            .padding(leftRight = 12.px, topBottom = 10.px)
            .backgroundColor(Colors.Transparent)
            .cursor(Cursor.Pointer)
    }
    hover { Modifier.backgroundColor(CustomSelectVars.SelectedOptionBackgroundColor.value()) }
}

val SelectedCustomSelectOptionVariant = CustomSelectOptionStyle.addVariant {
    base { Modifier.backgroundColor(CustomSelectVars.SelectedOptionBackgroundColor.value()) }
}

// ─── Components ───────────────────────────────────────────────────────────────

/**
 * Production-ready custom select dropdown component.
 *
 * Features:
 *   - Full keyboard navigation (arrow keys, Enter, Escape, Tab)
 *   - WAI-ARIA Listbox pattern compliance
 *   - Automatic up/down placement based on viewport space
 *   - Dismiss on outside click
 *   - Customizable via [CustomSelectVars] CSS variables
 *
 * @param id Unique HTML id for the container (used for keyboard/accessibility)
 * @param label Label text displayed above the field
 * @param placeholder Placeholder text shown when nothing is selected
 * @param items List of selectable items
 * @param selectedItem Currently selected item (null if none)
 * @param onItemSelect Callback: (index, item) -> Unit
 * @param modifier Styling modifier applied to the container
 * @param allowClearSelection When true, the placeholder is shown as a menu option that clears the selection.
 * Selecting it triggers [onItemSelect] with index `-1`.
 * @param valid Optional validation state. Set to `false` to show error border styling.
 * @param triggerVariant Optional extra [CssStyleVariant] for the trigger row, applied in both open and closed states
 */
@Composable
fun CustomSelect(
    id: String,
    label: String,
    placeholder: String = "Selecteer...",
    items: List<String>,
    selectedItem: String?,
    onItemSelect: (Int, String) -> Unit,
    modifier: Modifier = Modifier,
    allowClearSelection: Boolean = false,
    valid: Boolean? = null,
    triggerVariant: CssStyleVariant<CustomSelectTriggerKind>? = null,
) {
    val hasClearOption = allowClearSelection && placeholder.isNotBlank()
    val renderedItems = if (hasClearOption) listOf(placeholder) + items else items
    val selectedRenderedItem = selectedItem ?: if (hasClearOption) placeholder else null

    var isOpen by remember { mutableStateOf(false) }
    var openUpward by remember { mutableStateOf(false) }
    var focusedIndex by remember { mutableStateOf<Int?>(null) }
    var shouldFocusTrigger by remember { mutableStateOf(false) }

    // Keep latest callback values for DOM event handlers
    val latestItems by rememberUpdatedState(renderedItems)
    val latestSelectedItem by rememberUpdatedState(selectedRenderedItem)
    val latestHasClearOption by rememberUpdatedState(hasClearOption)
    val latestOnItemSelect by rememberUpdatedState(onItemSelect)

    // Focus option when needed
    LaunchedEffect(isOpen, focusedIndex) {
        if (isOpen && focusedIndex != null) {
            (document.getElementById("${id}-option-${focusedIndex}") as? HTMLElement)?.focus()
        }
    }

    // Return focus to trigger after item selection or Escape
    LaunchedEffect(shouldFocusTrigger) {
        if (shouldFocusTrigger) {
            (document.getElementById("${id}-trigger") as? HTMLElement)?.focus()
            shouldFocusTrigger = false
        }
    }

    // Dismiss on outside click
    DisposableEffect(isOpen) {
        if (!isOpen) return@DisposableEffect onDispose {}
        val handler: KeyboardHandler = h@{ event ->
            val clicked = event.target as? HTMLElement
            val container = document.getElementById(id) as? HTMLElement
            if (container != null && clicked != null && !container.contains(clicked)) {
                isOpen = false
                focusedIndex = null
            }
        }
        document.addEventListener("click", handler, true)
        onDispose { document.removeEventListener("click", handler, true) }
    }

    // Attach keyboard handler — uses lambdas so the handler always reads live state
    DisposableEffect(id) {
        val selectState = SelectState(
            getItems = { latestItems },
            getSelectedItem = { latestSelectedItem },
            getIsOpen = { isOpen },
            getFocusedIndex = { focusedIndex },
            onStateChange = { newOpen, newFocusedIdx ->
                if (newOpen && !isOpen) openUpward = shouldSelectOpenUpward("${id}-trigger")
                isOpen = newOpen
                focusedIndex = newFocusedIdx
            },
            onItemSelect = { idx, item ->
                val mappedIndex = if (latestHasClearOption) idx - 1 else idx
                latestOnItemSelect(mappedIndex, item)
            },
            onFocusTrigger = { shouldFocusTrigger = true },
        )
        val cleanup = attachKeyboardNavigation(id, selectState)
        onDispose { cleanup() }
    }

    Box(
        modifier = CustomSelectStyle.toModifier()
            .then(modifier)
            .attrsModifier { id(id) }
    ) {
        Column(
            modifier = Modifier.fillMaxWidth()
        ) {
            Label(attrs = Modifier.margin(bottom = 0.5.cssRem).attrsModifier { id("${id}-label") }.toAttrs()) {
                Text(label)
            }

            SelectTriggerButton(
                id = id,
                isOpen = isOpen,
                focusedIdx = focusedIndex,
                placeholder = placeholder,
                selectedItem = selectedItem,
                valid = valid,
                triggerVariant = triggerVariant,
                onToggle = {
                    if (!isOpen) {
                        openUpward = shouldSelectOpenUpward("${id}-trigger")
                        val selectedIdx = renderedItems.indexOf(selectedRenderedItem)
                        focusedIndex = if (selectedIdx >= 0) selectedIdx else if (renderedItems.isNotEmpty()) 0 else null
                    } else {
                        focusedIndex = null
                    }
                    isOpen = !isOpen
                }
            )
        }

        if (isOpen) {
            SelectMenu(
                id = id,
                items = renderedItems,
                selectedItem = selectedRenderedItem,
                openUpward = openUpward,
                onItemSelect = { idx, item ->
                    val mappedIndex = if (hasClearOption) idx - 1 else idx
                    latestOnItemSelect(mappedIndex, item)
                    focusedIndex = idx
                    isOpen = false
                    shouldFocusTrigger = true
                }
            )
        }
    }
}

@Composable
private fun SelectTriggerButton(
    id: String,
    isOpen: Boolean,
    focusedIdx: Int?,
    placeholder: String,
    selectedItem: String?,
    valid: Boolean?,
    triggerVariant: CssStyleVariant<CustomSelectTriggerKind>?,
    onToggle: () -> Unit,
) {
    val invalidStateModifier = if (valid == false) {
        Modifier
            .setVariable(CustomSelectVars.BorderColor, MaterialColorVars.Error.value())
            .setVariable(CustomSelectVars.BorderActiveColor, MaterialColorVars.Error.value())
    } else {
        Modifier
    }

    Row(
        modifier = CustomSelectTriggerStyle
            .toModifier(triggerVariant)
            .then(invalidStateModifier)
            .then(if (isOpen) activeBorderModifier() else Modifier)
            .attrsModifier {
                id("${id}-trigger")
                tabIndex(0)
                attr("role", "combobox")
                attr("aria-labelledby", "${id}-label")
                attr("aria-haspopup", "listbox")
                attr("aria-expanded", isOpen.toString())
                if (valid == false) {
                    attr("aria-invalid", "true")
                }
                if (isOpen) {
                    attr("aria-controls", "${id}-menu")
                }
                attr("aria-autocomplete", "none")
                if (isOpen && focusedIdx != null) {
                    attr("aria-activedescendant", "${id}-option-${focusedIdx}")
                }
            }
            .onClick { onToggle() },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            modifier = Modifier
                .color(
                    if (selectedItem != null) MaterialColorVars.OnSurface.value() else MaterialColorVars.OnSurfaceVariant.value()
                )
                .overflow(Overflow.Hidden)
                .whiteSpace(WhiteSpace.NoWrap)
                .textOverflow(TextOverflow.Ellipsis),
            text = selectedItem ?: placeholder
        )
        DsMaterialSymbols(
            modifier = Modifier
                .fontSize(20.px)
                .color(MaterialColorVars.OnSurfaceVariant.value()),
            icon = if (isOpen) "expand_less" else "expand_more"
        )
    }
}

@Composable
private fun SelectMenu(
    id: String,
    items: List<String>,
    selectedItem: String?,
    openUpward: Boolean,
    onItemSelect: (Int, String) -> Unit,
) {
    Column(
        modifier = CustomSelectMenuStyle.toModifier(
            if (openUpward) UpwardCustomSelectMenuVariant else DownwardCustomSelectMenuVariant
        ).attrsModifier {
            id("${id}-menu")
            attr("role", "listbox")
            attr("aria-labelledby", "${id}-label")
        }
    ) {
        items.forEachIndexed { index, item ->
            SelectOption(
                id = id,
                index = index,
                item = item,
                isSelected = item == selectedItem,
                onSelect = { onItemSelect(index, item) }
            )
        }
    }
}

@Composable
private fun SelectOption(
    id: String,
    index: Int,
    item: String,
    isSelected: Boolean,
    onSelect: () -> Unit,
) {
    Row(
        modifier = CustomSelectOptionStyle
            .toModifier(if (isSelected) SelectedCustomSelectOptionVariant else null)
            .attrsModifier {
                id("${id}-option-${index}")
                tabIndex(-1)
                attr("role", "option")
                attr("aria-selected", isSelected.toString())
            }
            .onClick { onSelect() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (isSelected) {
            DsMaterialSymbols(
                modifier = Modifier
                    .fontSize(16.px)
                    .color(MaterialColorVars.Primary.value()),
                icon = "check"
            )
        }
        SpanText(
            modifier = Modifier
                .color(MaterialColorVars.OnSurface.value())
                .padding(left = if (isSelected) 8.px else 24.px),
            text = item
        )
    }
}

// ─── Private utilities ────────────────────────────────────────────────────────

/**
 * Holds live-read lambdas for select state so the keyboard handler never reads stale values.
 * All properties delegate to lambdas that are captured at call site, ensuring they return
 * the current Compose state on every invocation.
 */
private class SelectState(
    private val getItems: () -> List<String>,
    private val getSelectedItem: () -> String?,
    private val getIsOpen: () -> Boolean,
    private val getFocusedIndex: () -> Int?,
    val onStateChange: (isOpen: Boolean, focusedIndex: Int?) -> Unit,
    val onItemSelect: (Int, String) -> Unit,
    val onFocusTrigger: () -> Unit,
) {
    val items: List<String> get() = getItems()
    val isOpen: Boolean get() = getIsOpen()
    val focusedIndex: Int? get() = getFocusedIndex()
    val itemCount: Int get() = getItems().size
    val selectedOptionIndex: Int get() = getItems().indexOf(getSelectedItem())

    fun getNextFocusIndex(key: String): Int? {
        if (itemCount == 0) return null
        val current = getFocusedIndex() ?: return if (key == "ArrowDown") 0 else itemCount - 1
        return when (key) {
            "ArrowDown" -> (current + 1) % itemCount
            else -> (current - 1 + itemCount) % itemCount
        }
    }
}

private fun shouldSelectOpenUpward(triggerId: String): Boolean {
    val el = document.getElementById(triggerId) as? HTMLElement ?: return false
    return (window.innerHeight - el.getBoundingClientRect().bottom) < SELECT_MENU_HEIGHT_ESTIMATE
}

private fun attachKeyboardNavigation(
    containerId: String,
    state: SelectState
): () -> Unit {
    val container = document.getElementById(containerId) as? HTMLElement ?: return {}

    val handler: KeyboardHandler = h@{ event ->
        val key = event.asDynamic().key as? String ?: return@h

        when (key) {
            "ArrowDown", "ArrowUp" -> {
                if (state.itemCount == 0) return@h
                event.preventDefault()
                if (!state.isOpen) {
                    state.onStateChange(true, if (key == "ArrowDown") 0 else state.itemCount - 1)
                } else {
                    state.onStateChange(state.isOpen, state.getNextFocusIndex(key))
                }
            }

            "Enter", " " -> {
                event.preventDefault()
                if (!state.isOpen) {
                    val idx = if (state.selectedOptionIndex >= 0) state.selectedOptionIndex else 0
                    state.onStateChange(true, if (state.itemCount > 0) idx else null)
                } else if (state.focusedIndex != null) {
                    state.onItemSelect(state.focusedIndex ?: 0, state.items[state.focusedIndex ?: 0])
                    state.onStateChange(false, null)
                    state.onFocusTrigger()
                }
            }

            "Escape" -> {
                event.preventDefault()
                state.onStateChange(false, null)
                state.onFocusTrigger()
            }

            "Tab" -> {
                if (state.isOpen) state.onStateChange(false, null)
            }
        }
    }

    container.addEventListener("keydown", handler)
    return { container.removeEventListener("keydown", handler) }
}
